import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import {
  Observable,
  BehaviorSubject,
  Subject,
  tap,
  finalize,
  catchError,
  of,
  map,
  throwError,
  retry,
} from "rxjs";
import { environment } from "src/environments/environment";
import { UserUpdateService } from "./user-update.service";
import { AvatarService } from "./avatar.service";
import { Router, NavigationEnd } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslationService } from "../../i18n/translation.service";
import { TokenExpiredModalComponent } from "src/app/_metronic/layout/components/modals/token-expired-modal/token-expired-modal.component";
import { SessionHeartbeatService } from "src/app/_core/_services/heartbeat/hearbeat.service";
import { CampaignsService } from "src/app/_core/_services/campaigns/campaigns.service";
import { WebSocketService } from "src/app/_core/_services/web-socket.service";

interface LoginResponse {
  message: string;
  userData: UserType;
  expiresAt: number;
}

interface RefreshTokenResponse {
  message: string;
  token?: string;
  expiresAt: number;
}

export interface UserType {
  id: number;
  api_id: number;
  userId: number;
  name: string;
  surname: string;
  email: string;
  role: string;
  avatar: string | null;
  company_id: number;
  timezone: string;
  language: string;
  organization_name?: string;
}

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(
    this.checkInitialAuthState()
  );
  private apiUri = `${environment.apiUri}`;
  private userData: UserType | null = null;
  private userRole = new BehaviorSubject<string>("");
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private currentUserSubject = new BehaviorSubject<UserType | null>(null);
  private isRefreshing: boolean = false;
  public isLoggedIn = true;

  // Aktywność użytkownika przedłuża sesję — odświeżamy token nie częściej niż co 2 min.
  // Throttle współdzielony między kartami przez localStorage("lastRenewAt"), żeby wiele kart
  // nie odświeżało równolegle i licznik był spójny.
  private readonly RENEW_THROTTLE_MS = 120_000;

  /** Emitowany gdy token został pomyślnie odświeżony — używany przez AuthInterceptor */
  public readonly tokenRefreshed$ = new Subject<void>();
  /** Emitowany gdy sesja wygasła (wylogowanie) — używany przez AuthInterceptor */
  public readonly tokenExpired$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private avatarService: AvatarService,
    private webSocketService: WebSocketService,
    private router: Router,
    private modalService: NgbModal,
    private userUpdateService: UserUpdateService,
    private translationService: TranslationService,
    private campaignService: CampaignsService,
    private sessionHeartbeatService: SessionHeartbeatService
  ) {
    this.initializeUserData();
    this.sessionHeartbeatService.sessionExpiring$.subscribe((isExpiring) => {
      if (isExpiring) {
        this.handleTokenExpiry();
      }
    });
    this.setupActivityTracking();
    this.setupCrossTabSync();
  }

  // Synchronizacja sesji między kartami (localStorage + storage event):
  // - odświeżenie tokenu w jednej karcie → wszystkie karty aktualizują timer,
  // - wylogowanie/wygaśnięcie w jednej karcie → natychmiastowe wylogowanie we wszystkich.
  private setupCrossTabSync(): void {
    if (typeof window === "undefined") {
      return;
    }
    window.addEventListener("storage", (e: StorageEvent) => {
      if (e.key === "userData" && e.newValue === null) {
        // Inna karta wylogowała (usunęła userData) — wyloguj lokalnie bez ponownego HTTP.
        this.handleCrossTabLogout();
      } else if (e.key === "tokenExpiresAt" && e.newValue) {
        // Inna karta odświeżyła token — zsynchronizuj licznik sesji.
        const expiresAt = Number(e.newValue);
        if (!isNaN(expiresAt) && this.userData) {
          this.sessionHeartbeatService.start(expiresAt);
        }
      }
    });
  }

  private handleCrossTabLogout(): void {
    if (!this.isLoggedIn && !this.userData) {
      return; // już wylogowany
    }
    this.isLoggedIn = false;
    this.isRefreshing = false;
    if (this.modalService.hasOpenModals()) {
      this.modalService.dismissAll();
    }
    // Czyść tylko stan w pamięci/serwisach — localStorage usunęła już inna karta.
    this.userData = null;
    this.userRole.next("");
    this.currentUserSubject.next(null);
    this.avatarService.resetAvatarUrl();
    this.isAuthenticatedSubject.next(false);
    this.sessionHeartbeatService.stop();
    this.campaignService.setSelectedCampaign(null);
    this.router.navigate(["/auth/login"], { queryParams: { sessionExpired: "true" } });
  }

  /** Czyści httpOnly cookie sesji po stronie backendu (np. przy wejściu na ekran logowania). */
  clearAuthCookie(): void {
    this.http
      .post(`${this.apiUri}/auth/logout`, {}, { withCredentials: true })
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  // Każda aktywność (klik, klawisz, nawigacja) przedłuża sesję — token jest odświeżany
  // (throttled), więc dopóki user jest aktywny, sesja się nie kończy.
  private setupActivityTracking(): void {
    const onActivity = () => this.onUserActivity();
    if (typeof document !== "undefined") {
      document.addEventListener("click", onActivity, { passive: true });
      document.addEventListener("keydown", onActivity, { passive: true });
    }
    this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.onUserActivity();
      }
    });
  }

  private onUserActivity(): void {
    if (!this.isLoggedIn || !this.userData) {
      return;
    }
    // Gdy modal "Sesja wygasa" jest otwarty — wymagaj jawnego kliknięcia "Przedłuż".
    if (this.sessionHeartbeatService.modalVisible || this.isRefreshing) {
      return;
    }
    const now = Date.now();
    // Throttle współdzielony przez localStorage — jeśli KTÓRAKOLWIEK karta odświeżyła
    // niedawno, pomiń (unika równoległych odświeżeń i rozjazdu licznika między kartami).
    const lastRenew = Number(localStorage.getItem("lastRenewAt") || "0");
    if (now - lastRenew < this.RENEW_THROTTLE_MS) {
      return;
    }
    localStorage.setItem("lastRenewAt", String(now)); // rezerwacja okna dla wszystkich kart
    this.isRefreshing = true;
    this.refreshToken().subscribe({
      next: () => {
        this.isRefreshing = false;
      },
      error: () => {
        this.isRefreshing = false;
      },
    });
  }

  get isLoading$(): Observable<boolean> {
    return this.isLoadingSubject.asObservable();
  }

  get currentUser$(): Observable<UserType | null> {
    return this.currentUserSubject.asObservable();
  }

  get currentUserValue(): UserType | null {
    return this.userData;
  }

  private initializeUserData(): void {
    const storedUserData = localStorage.getItem("userData");
    const storedExpiresAt = localStorage.getItem("tokenExpiresAt");

    if (storedUserData && storedExpiresAt) {
      const expiresAt = Number(storedExpiresAt);

      if (expiresAt > Date.now()) {
        const userData = JSON.parse(storedUserData) as UserType;
        this.setUserData(userData);
        this.sessionHeartbeatService.start(expiresAt);
        if (userData.language) {
          this.translationService.setLanguage(userData.language);
        }
      } else {
        // Token wygasł podczas nieobecności — wyczyść bez wysyłania HTTP logout
        this.clearAuthenticationStatus();
      }
    } else {
      this.clearAuthenticationStatus();
    }
  }

  private clearAuthenticationStatus(): void {
    this.isLoggedIn = false;
    this.isAuthenticatedSubject.next(false);
    this.clearUserData();
  }

  private checkInitialAuthState(): boolean {
    return !!localStorage.getItem("userData");
  }

  private setUserData(data: UserType): void {
    this.clearPreviousSession();
    this.userData = data;
    this.userRole.next(data.role);
    this.currentUserSubject.next(data);
    this.isLoggedIn = true;

    localStorage.setItem("userData", JSON.stringify(data));

    if (data.language) {
      this.translationService.setLanguage(data.language);
    }

    if (data.avatar) {
      this.avatarService.updateAvatarUrl(data.avatar);
    } else {
      this.avatarService.resetAvatarUrl();
    }

    this.updateAuthenticationStatus();
    // Timer sesji startowany jest z login() lub initializeUserData() — nie tutaj
  }

  updateCurrentUser(data: Partial<UserType>): void {
    if (this.userData) {
      const updatedUserData = { ...this.userData, ...data };
      this.setUserData(updatedUserData);
    }
  }

  register(userData: any): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.http.post(`${this.apiUri}/auth/register`, userData).pipe(
      tap((response: any) => {
        if (response.userData) {
          this.setUserData(response.userData);
          this.updateAuthenticationStatus();
        }
      }),
      catchError((err) => {
        console.error("Registration error:", err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  login(credentials: {
    email: string;
    password: string;
  }): Observable<LoginResponse> {
    this.isLoadingSubject.next(true);

    return this.http
      .post<LoginResponse>(`${this.apiUri}/auth/login`, credentials, {
        withCredentials: true,
      })
      .pipe(
        tap((response: LoginResponse) => {
          if (response.userData) {
            this.setUserData(response.userData);
            this.updateAuthenticationStatus();

            if (response.userData.language) {
              this.translationService.setLanguage(response.userData.language);
            }

            // Zapisz timestamp wygaśnięcia i startuj timer
            const expiresAt = response.expiresAt || (Date.now() + 3600000);
            localStorage.setItem("tokenExpiresAt", String(expiresAt));
            this.sessionHeartbeatService.start(expiresAt);

            this.campaignService.setSelectedCampaign(null);

            console.log("Logging in: Connecting WebSocket...");
            this.webSocketService.connect();
          }
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            console.error("Login failed: Invalid credentials.");
          } else {
            console.error("Login failed:", error);
          }
          return throwError(error);
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  forgotPassword(email: string): Observable<boolean> {
    return this.http
      .post<{ message: string }>(`${this.apiUri}/auth/forgot-password`, {
        email,
      })
      .pipe(
        map(() => true),
        catchError((error) => {
          console.error("Forgot password error:", error);
          return of(false);
        })
      );
  }

  getUserByToken(): Observable<boolean> {
    if (!this.userData) {
      return of(false);
    }

    return this.http
      .get<{ message: string }>(`${this.apiUri}/auth/verify-token`, {
        withCredentials: true,
      })
      .pipe(
        map(() => {
          this.updateAuthenticationStatus();
          return true;
        }),
        catchError((error) => {
          if (error.status === 401) {
            console.error(
              "Token verification failed: User is not authenticated."
            );
            this.clearAuthenticationStatus();
          }
          return of(false);
        })
      );
  }

  getApiUserIdFromAuthService(): number | null {
    return this.userData ? this.userData.api_id : null;
  }

  getUserId(id: number): Observable<{ userId: string }> {
    return this.http.get<{ userId: string }>(
      `${this.apiUri}/user/getUserId/${id}`
    );
  }

  private updateAuthenticationStatus(): void {
    const isAuthenticated = !!this.userData;
    this.isAuthenticatedSubject.next(isAuthenticated);
  }

  getIsAuthenticated(): Observable<boolean> {
    return this.isAuthenticatedSubject.asObservable();
  }

  refreshToken(): Observable<void> {
    return this.http
      .post<RefreshTokenResponse>(
        `${this.apiUri}/auth/refresh-token`,
        {},
        { withCredentials: true }
      )
      .pipe(
        tap((response) => {
          if (response.expiresAt) {
            localStorage.setItem("tokenExpiresAt", response.expiresAt.toString());
            this.sessionHeartbeatService.start(response.expiresAt);
          }
          this.tokenRefreshed$.next();
          console.log("Token refreshed successfully.");
        }),
        map(() => void 0),
        catchError((error) => {
          console.error("Failed to refresh token:", error);
          this.logout(true);
          return throwError(() => new Error("Failed to refresh token"));
        })
      );
  }

  changePassword(
    currentPassword: string,
    newPassword: string
  ): Observable<any> {
    return this.http
      .put(
        `${this.apiUri}/auth/change-password`,
        { currentPassword, newPassword },
        { withCredentials: true }
      )
      .pipe(
        tap((response: any) => {
          console.log("Password change response:", response);
        }),
        catchError((error) => {
          console.error("Password change error:", error);
          return throwError(error);
        })
      );
  }

  logout(sessionExpired: boolean = false): void {
    console.log("Logging out user...");

    // Zamknij otwarte modale
    if (this.modalService.hasOpenModals()) {
      this.modalService.dismissAll();
    }

    this.isLoggedIn = false;
    this.isRefreshing = false;

    this.clearUserData();

    this.campaignService.setSelectedCampaign(null);

    // Wyślij żądanie wylogowania do backendu (backend czyści httpOnly cookie)
    this.http
      .post(`${this.apiUri}/auth/logout`, {}, { withCredentials: true })
      .pipe(
        retry(3),
        finalize(() => {
          this.clearUserData();
          this.navigateAfterLogout(sessionExpired);
        }),
        catchError((error) => {
          console.error("HTTP request failed during logout:", error);
          return of(null);
        })
      )
      .subscribe();
  }

  private navigateAfterLogout(sessionExpired: boolean): void {
    const queryParams = sessionExpired ? { sessionExpired: "true" } : {};
    this.router.navigate(["/auth/login"], { queryParams });
  }

  hasRole(roles: string[]): boolean {
    return roles.includes(this.userData?.role || "");
  }

  private clearUserData(): void {
    // Selektywne czyszczenie — nie usuwamy niezwiązanych kluczy z localStorage
    localStorage.removeItem("userData");
    localStorage.removeItem("tokenExpiresAt");
    localStorage.removeItem("avatarUrl");
    localStorage.removeItem("lastRenewAt");
    this.userData = null;
    this.userRole.next("");
    this.currentUserSubject.next(null);
    this.avatarService.resetAvatarUrl();
    this.isAuthenticatedSubject.next(false);
    this.sessionHeartbeatService.stop();
  }

  private clearPreviousSession(): void {
    this.sessionHeartbeatService.stopHeartbeat();
    if (this.modalService.hasOpenModals()) {
      this.modalService.dismissAll();
    }
    this.isRefreshing = false;
  }

  handleTokenExpiry(): void {
    if (!this.isLoggedIn) {
      console.log("User is logged out, skipping token expiry modal.");
      return;
    }

    if (this.modalService.hasOpenModals()) {
      console.log("Modal already open, skipping duplicate modal.");
      return;
    }

    console.log("Opening session expiry modal.");
    this.sessionHeartbeatService.modalVisible = true;

    const modalRef = this.modalService.open(TokenExpiredModalComponent, {
      backdrop: "static",
      keyboard: false,
      centered: true,
      size: "md",
    });

    modalRef.result
      .then((result) => {
        console.log("Modal result:", result);

        if (result === "extend") {
          this.refreshToken().subscribe({
            next: () => {
              console.log("Token refreshed successfully.");
            },
            error: (error) => {
              console.error("Failed to refresh token:", error);
              this.tokenExpired$.next();
              this.logout(true);
            },
          });
        } else {
          console.log("User chose to logout.");
          this.tokenExpired$.next();
          this.logout(true);
        }
      })
      .catch((reason) => {
        console.log("Modal dismissed:", reason);
        this.tokenExpired$.next();
        this.logout(true);
      })
      .finally(() => {
        this.sessionHeartbeatService.modalVisible = false;
      });
  }
}
